// frontend/src/pages/UserGuide.js
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  BookOpenCheck,
  Search,
  ChevronRight,
  ArrowRight,
  Info,
  Lightbulb,
  AlertTriangle,
  Image as ImageIcon,
  Rocket,
  LayoutDashboard,
  Clock,
  CalendarPlus,
  CalendarDays,
  CalendarClock,
  User,
  Users,
  Laptop,
  FileText,
  CheckSquare,
  LifeBuoy,
  Bell,
  Settings,
  Moon,
  BookUser,
  QrCode,
  ShieldAlert,
  Repeat,
  UsersRound,
  Mail,
  Brain,
  Puzzle,
  DollarSign,
  GraduationCap,
  Languages,
  Plane,
  Truck,
  UserMinus,
  ChevronsDownUp,
  ChevronsUpDown,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import AccordionSection from '../components/UI/AccordionSection';
import { CountBadge } from '../components/UI/CountBadge';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import PillSelect from '../components/UI/PillSelect';
import { useAuth } from '../contexts/AuthContext';
import {
  useAssignedOffboardingTasks,
  useMyOffboardingCase,
} from '../hooks/useOffboardingCases';
import { useTerminationCapabilities } from '../hooks/useTerminationRequests';
import { useTransportPortalAccess } from '../hooks/useTransportPortalAccess';
import { useUserGuideLanguage } from '../hooks/useUserGuideLanguage';
import { USER_GUIDE_SECTIONS, USER_GUIDE_CATEGORIES } from '../config/userGuideContent';
import { USER_GUIDE_TRANSLATIONS } from '../config/userGuideTranslations';
import { getUserGuideUi } from '../config/userGuideUiTranslations';
import {
  GUIDE_LANGUAGES,
  DEFAULT_GUIDE_LANG,
} from '../config/userGuideLanguages';

// ── Localisation ──────────────────────────────────────────────────────────
// English (userGuideContent.js) is canonical. A locale's overrides
// (userGuideTranslations.js) are deep-merged on top, field-by-field, so any
// untranslated string falls back to English instead of rendering blank.
const localizeBlock = (block, tBlock) => {
  if (!tBlock) return block;
  const next = { ...block };
  if (tBlock.heading) next.heading = tBlock.heading;
  if (tBlock.text) next.text = tBlock.text;
  if (tBlock.caption) next.caption = tBlock.caption;
  if (tBlock.alt) next.alt = tBlock.alt;
  if (Array.isArray(block.items) && Array.isArray(tBlock.items)) {
    next.items = block.items.map((item, i) => tBlock.items[i] ?? item);
  }
  return next;
};

const localizeSection = (section, tSection) => {
  if (!tSection) return section;
  return {
    ...section,
    title: tSection.title || section.title,
    summary: tSection.summary || section.summary,
    routeLabel: tSection.routeLabel || section.routeLabel,
    blocks: (section.blocks || []).map((b, i) => localizeBlock(b, tSection.blocks?.[i])),
  };
};

const localizeSections = (sections, locale) => {
  const t = USER_GUIDE_TRANSLATIONS[locale];
  if (locale === DEFAULT_GUIDE_LANG || !t) return sections;
  return sections.map((s) => localizeSection(s, t[s.id]));
};

// Map config icon names → lucide components (config stays serialisable).
const ICONS = {
  Rocket,
  LayoutDashboard,
  Clock,
  CalendarPlus,
  CalendarDays,
  CalendarClock,
  User,
  Users,
  Laptop,
  FileText,
  CheckSquare,
  LifeBuoy,
  Bell,
  Settings,
  Moon,
  BookUser,
  QrCode,
  ShieldAlert,
  Repeat,
  UsersRound,
  Mail,
  Brain,
  Puzzle,
  DollarSign,
  BookOpenCheck,
  GraduationCap,
  Plane,
  Truck,
  UserMinus,
  BookOpen,
};

// Section colour drives the icon chip + accents. Mirrors the palette used in
// SelfService.js so the guide feels native to the portal.
const COLORS = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-700', ring: 'focus-visible:ring-blue-500' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', ring: 'focus-visible:ring-indigo-500' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-700', ring: 'focus-visible:ring-teal-500' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-700', ring: 'focus-visible:ring-violet-500' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-700', ring: 'focus-visible:ring-amber-500' },
  green: { bg: 'bg-green-100', text: 'text-green-700', ring: 'focus-visible:ring-green-500' },
  rose: { bg: 'bg-rose-100', text: 'text-rose-700', ring: 'focus-visible:ring-rose-500' },
};

// Visibility rules, used at BOTH the section level (`section.audience`) and the
// block level (`block.audience`). A block with no `audience` inherits its
// section's visibility (i.e. shows to whoever can see the section). This lets a
// shared section (e.g. Applying for Leave) hide worker-only or staff-only
// paragraphs so each classification only sees what applies to them.
// Keep in sync with the role logic used across the app (DashboardLayout/Dashboard).
const AUDIENCE = {
  everyone: () => true,
  notExecutiveDirector: (u) => u?.employment_classification !== 'executive_director',
  // Classification matchers (employment_classification values from classificationConfig.js)
  workersOnly: (u) => u?.employment_classification === 'worker',
  staffOnly: (u) => u?.employment_classification === 'staff',
  nonWorker: (u) => u?.employment_classification !== 'worker',
  approvers: (u) =>
    !!(
      u?.is_reporting_officer ||
      u?.is_manager ||
      u?.is_trade_leader ||
      u?.is_hod ||
      (u?.team_members?.length > 0)
    ),
  // "My Team" dashboard is shown only to ROs and HODs (mirrors DashboardLayout).
  reportingLine: (u) => !!(u?.is_reporting_officer || u?.is_hod),
  courseApprovers: (u) =>
    !!(
      u?.is_hod ||
      u?.is_superuser ||
      u?.scopes?.includes('confidential')
    ),
  hrAdmins: (u) => !!(u?.is_superuser || u?.scopes?.includes('confidential')),
  assetCustodians: (u) => !!u?.is_asset_custodian,
  transportUsers: (_u, context) => !!context?.hasTransportAccess,
  lifecycleParticipants: (u, context) =>
    !!(
      context?.hasOwnOffboarding ||
      context?.hasClearanceTasks ||
      context?.hasResignationNotice ||
      u?.can_act_work_pass_decision ||
      u?.can_submit_termination_request
    ),
  ownOffboarding: (_u, context) => !!context?.hasOwnOffboarding,
  clearanceOwners: (_u, context) => !!context?.hasClearanceTasks,
  workPassActors: (u) => !!u?.can_act_work_pass_decision,
  terminationActors: (u) => !!u?.can_submit_termination_request,
  resignationEmployees: (_u, context) => !!context?.hasResignationNotice,
};

const NOTE_STYLES = {
  info: { wrap: 'bg-blue-50 border-blue-200 text-blue-800', icon: Info, iconColor: 'text-blue-500' },
  tip: { wrap: 'bg-green-50 border-green-200 text-green-800', icon: Lightbulb, iconColor: 'text-green-600' },
  warning: { wrap: 'bg-amber-50 border-amber-200 text-amber-800', icon: AlertTriangle, iconColor: 'text-amber-600' },
};

// Flatten a section's text so search can match steps/notes, not just titles.
const sectionHaystack = (section) => {
  const parts = [section.title, section.summary];
  (section.blocks || []).forEach((b) => {
    if (b.heading) parts.push(b.heading);
    if (b.text) parts.push(b.text);
    if (Array.isArray(b.items)) parts.push(...b.items);
  });
  return parts.join(' ').toLowerCase();
};

// ── Search ──────────────────────────────────────────────────────────────
// Employees rarely type the exact wording used in the guide. Each group below
// lists interchangeable terms; typing any one expands the search to all of
// them, so "salary" finds the Payslip section, "OT" finds overtime, "MC"
// finds medical certificate, etc. Keep groups small and unambiguous.
const SEARCH_SYNONYM_GROUPS = [
  ['payslip', 'pay slip', 'salary', 'pay', 'payroll', 'wage', 'wages', 'cpf'],
  ['overtime', 'ot'],
  ['medical certificate', 'mc', 'sick leave', 'sick', 'hospitalisation'],
  ['login', 'log in', 'sign in', 'signin', 'password', 'forgot password'],
  ['clock in', 'clock out', 'attendance', 'punch', 'timesheet'],
  ['annual leave', 'al', 'vacation', 'holiday', 'time off', 'day off'],
  ['home leave', 'hl', 'flight', 'go home'],
  ['off in lieu', 'oil', 'time off in lieu', 'in lieu'],
  ['reporting officer', 'ro', 'approver', 'manager', 'supervisor', 'boss', 'approve'],
  ['leave calendar', 'who is on leave', 'team leave'],
  ['profile', 'my details', 'personal details', 'emergency contact', 'address'],
  ['assets', 'laptop', 'equipment', 'devices', 'phone', 'sim'],
  ['notifications', 'alerts', 'preferences', 'whatsapp', 'email'],
  ['nightwork', 'night shift', 'recovery day', 'recovery', 'next morning', 'next day'],
  ['rest day', 'sunday', 'public holiday', 'ph', 'ph work'],
  ['passport', 'passport request'],
  ['whistleblowing', 'misconduct', 'report', 'anonymous'],
  ['name card', 'business card', 'digital card'],
  ['big five', 'personality', 'disc', 'work style', 'assessment'],
  ['marriage leave', 'wedding', 'married'],
  ['maternity', 'paternity', 'childcare', 'birth', 'baby', 'parental'],
  ['petty cash', 'claim', 'reimbursement', 'expense'],
  ['handbook', 'employee handbook', 'company policy', 'policies', 'acknowledgement', 'code of conduct'],
  ['training', 'course', 'course request', 'learning', 'sponsorship', 'certificate'],
  ['overseas travel', 'travel notice', 'holiday travel', 'rest day travel', 'day off travel'],
  ['offboarding', 'clearance', 'leaving', 'last day', 'departure'],
  ['work pass', 'work permit', 'renewal', 'expiry', 'retain'],
  ['resignation', 'resign', 'termination', 'notice period'],
  ['transport', 'booking', 'vehicle', 'driver'],
  ['admin', 'hr admin', 'human resources', 'employee management', 'system settings'],
  ['asset custodian', 'asset admin', 'inventory', 'fleet', 'disposal', 'endpoint security'],
];

// Expand a single typed token to itself + any synonym-group members. A token
// only triggers a group when it equals a group term or is a whole word inside
// one (e.g. "medical" → the MC group), never via loose substring (so short
// abbreviations like "al" don't leak into unrelated groups).
const expandSearchToken = (token) => {
  const out = new Set([token]);
  SEARCH_SYNONYM_GROUPS.forEach((group) => {
    const hit = group.some(
      (term) => term === token || term.split(' ').includes(token)
    );
    if (hit) group.forEach((term) => out.add(term));
  });
  return [...out];
};

const tokenizeQuery = (q) => q.trim().toLowerCase().split(/\s+/).filter(Boolean);

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Build a case-insensitive, Unicode-aware whole-term regex. JavaScript's `\b`
// only understands ASCII word characters, so it breaks search/highlighting for
// Bangla, Devanagari, Tamil and Burmese. Unicode letter/number boundaries keep
// short English abbreviations precise while supporting every guide locale.
const buildTermRegex = (terms) => {
  const alt = [...new Set((terms || []).filter(Boolean).map((t) => t.toLowerCase()))]
    .sort((a, b) => b.length - a.length)
    .map((term) => {
      const escaped = escapeRegExp(term);
      const isAscii = [...term].every((character) => character.codePointAt(0) <= 0x7f);
      return isAscii
        ? `(?<![\\p{L}\\p{N}])${escaped}(?:es|s)?(?![\\p{L}\\p{N}])`
        : escaped;
    })
    .join('|');
  return alt ? new RegExp(`((?:${alt}))`, 'iu') : null;
};

// Every typed token must match (AND), but each token matches if ANY of its
// synonyms appears as a whole word — so word order and vocabulary differences
// don't matter, while substring noise ("overtime" matching "notifications")
// does not creep in.
const sectionMatchesQuery = (haystack, tokens) =>
  tokens.every((tok) => {
    const re = buildTermRegex(expandSearchToken(tok));
    return re ? re.test(haystack) : false;
  });

// The set of terms to visually highlight = all synonym expansions of all tokens.
const buildHighlightTerms = (tokens) => {
  const terms = new Set();
  tokens.forEach((tok) => expandSearchToken(tok).forEach((t) => terms.add(t)));
  return [...terms];
};

// Wrap matched terms in <mark> so users see exactly why a result matched.
// Matching is word-boundary aware (see buildTermRegex), so only real words are
// highlighted — never mid-word fragments.
const Highlighted = ({ text, terms }) => {
  const str = String(text ?? '');
  const pattern = useMemo(() => buildTermRegex(terms), [terms]);
  if (!pattern || !str) return str;
  // A single capture group means split() interleaves [text, match, text, ...],
  // so every odd index is a matched word to highlight.
  const parts = str.split(new RegExp(pattern.source, 'giu'));
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="bg-yellow-200 text-inherit rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

const Block = ({ block, terms = [], ui }) => {
  if (block.type === 'steps') {
    return (
      <div>
        {block.heading && (
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            <Highlighted text={block.heading} terms={terms} />
          </h4>
        )}
        <ol className="space-y-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <CountBadge count={i + 1} theme="employee" size="md" className="flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700 leading-relaxed">
                <Highlighted text={item} terms={terms} />
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  if (block.type === 'list') {
    return (
      <div>
        {block.heading && (
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            <Highlighted text={block.heading} terms={terms} />
          </h4>
        )}
        <ul className="space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <ChevronRight className="flex-shrink-0 w-4 h-4 text-gray-400 mt-0.5" />
              <span className="text-sm text-gray-700 leading-relaxed">
                <Highlighted text={item} terms={terms} />
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (block.type === 'note') {
    const style = NOTE_STYLES[block.tone] || NOTE_STYLES.info;
    const NoteIcon = style.icon;
    return (
      <div className={`flex gap-3 rounded-xl border p-3 ${style.wrap}`}>
        <NoteIcon className={`flex-shrink-0 w-5 h-5 ${style.iconColor}`} />
        <p className="text-sm leading-relaxed">
          <Highlighted text={block.text} terms={terms} />
        </p>
      </div>
    );
  }

  if (block.type === 'image') {
    // Render the real screenshot once a `src` is supplied; until then show a
    // tidy placeholder slot so the layout reads intentionally, not broken.
    if (block.src) {
      return (
        <figure className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
          <img
            src={block.src}
            alt={block.alt || block.caption || ''}
            loading="lazy"
            className="w-full h-auto"
          />
          {block.caption && (
            <figcaption className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    }
    return (
      <figure className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
        <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <figcaption className="text-xs text-gray-400">
          {block.caption || block.alt || ui.screenshotSoon}
        </figcaption>
      </figure>
    );
  }

  return null;
};

const GuideSection = ({ section, expanded, onToggle, registerRef, terms = [], ui }) => {
  const colors = COLORS[section.color] || COLORS.blue;
  const Icon = ICONS[section.icon] || BookOpen;

  // DS: collapsible sections must use the shared AccordionSection (employee
  // theme). The wrapper preserves the hash-anchor id + scroll offset + ref the
  // page uses for quick-nav, which AccordionSection itself does not forward.
  return (
    <div id={section.id} ref={registerRef} className="scroll-mt-24">
      <AccordionSection
        theme="employee"
        expanded={expanded}
        onToggle={onToggle}
        contentVariant="flush"
        icon={<Icon className={`w-4 h-4 ${colors.text}`} />}
        iconBg={colors.bg}
        title={
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">
              <Highlighted text={section.title} terms={terms} />
            </h2>
            <p className="text-sm text-gray-500 mt-0.5 font-normal">
              <Highlighted text={section.summary} terms={terms} />
            </p>
          </div>
        }
      >
        <div className="px-4 sm:px-5 pt-4 pb-5 space-y-6 border-t border-gray-100">
          {section.blocks.map((block, i) => (
            <Block key={i} block={block} terms={terms} ui={ui} />
          ))}

          {section.route && (
            <Link
              to={section.route}
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 group"
            >
              {section.routeLabel || ui.openFeature}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
      </AccordionSection>
    </div>
  );
};

const UserGuide = () => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [openIds, setOpenIds] = useState(() => new Set());
  const [activeCategory, setActiveCategory] = useState('all');
  const sectionRefs = useRef({});
  const { locale, changeLocale } = useUserGuideLanguage(user);
  const ui = useMemo(() => getUserGuideUi(locale), [locale]);
  const { data: transportPortalAccess } = useTransportPortalAccess({
    enabled: !!user,
  });
  const { data: myOffboardingCase } = useMyOffboardingCase({
    enabled: !!user,
  });
  const { data: assignedOffboardingTasks = [] } = useAssignedOffboardingTasks({
    enabled: !!user,
  });
  const { data: terminationCapabilities } = useTerminationCapabilities({
    enabled: !!user,
  });
  const audienceContext = useMemo(
    () => ({
      hasTransportAccess: transportPortalAccess?.has_transport_access === true,
      hasOwnOffboarding: !!myOffboardingCase,
      hasClearanceTasks: assignedOffboardingTasks.length > 0,
      hasResignationNotice:
        (terminationCapabilities?.pending_resignation_ack || 0) > 0 ||
        terminationCapabilities?.has_pending_resignation_ack === true,
    }),
    [
      assignedOffboardingTasks.length,
      myOffboardingCase,
      terminationCapabilities?.has_pending_resignation_ack,
      terminationCapabilities?.pending_resignation_ack,
      transportPortalAccess?.has_transport_access,
    ]
  );

  // Options for the PillSelect switcher. Untranslated languages are shown as
  // disabled "(soon)" entries so the roadmap is visible without rendering
  // broken English-in-disguise.
  const languageOptions = useMemo(
    () =>
      GUIDE_LANGUAGES.map((l) => ({
        value: l.code,
        label: l.ready ? l.nativeLabel : `${l.nativeLabel} (soon)`,
        disabled: !l.ready,
      })),
    []
  );

  // Only show sections relevant to this user's role/classification.
  const visibleSections = useMemo(
    () =>
      USER_GUIDE_SECTIONS.filter((s) =>
        (AUDIENCE[s.audience] || AUDIENCE.everyone)(user, audienceContext)
      ),
    [audienceContext, user]
  );

  // Apply translations (English fallback per-string), THEN filter blocks by
  // their per-block audience. Order matters: translations are index-aligned to
  // the full block list (userGuideTranslations.js), so we must localize before
  // dropping classification-specific blocks. Search + quick-nav consume this,
  // so hidden blocks are also excluded from search results.
  const localizedSections = useMemo(() => {
    const localized = localizeSections(visibleSections, locale);
    return localized.map((s) => ({
      ...s,
      blocks: (s.blocks || []).filter(
        (b) => (AUDIENCE[b.audience] || AUDIENCE.everyone)(user, audienceContext)
      ),
    }));
  }, [audienceContext, visibleSections, locale, user]);

  const hasEnglishFallbackTopics =
    locale !== DEFAULT_GUIDE_LANG &&
    localizedSections.some((section) => section.translationStatus === 'english-fallback');

  // Order the visible sections by their category (and order within category),
  // so the "All" tab reads top-to-bottom in the same grouping as the tabs.
  const orderedSections = useMemo(() => {
    const byId = new Map(localizedSections.map((s) => [s.id, s]));
    const ordered = [];
    const seen = new Set();
    USER_GUIDE_CATEGORIES.forEach((cat) => {
      cat.sectionIds.forEach((id) => {
        const s = byId.get(id);
        if (s) {
          ordered.push(s);
          seen.add(id);
        }
      });
    });
    // Safety net: any section not assigned to a category still shows.
    localizedSections.forEach((s) => {
      if (!seen.has(s.id)) ordered.push(s);
    });
    return ordered;
  }, [localizedSections]);

  // Only show category tabs that actually have visible sections for this user.
  const visibleCategories = useMemo(
    () =>
      USER_GUIDE_CATEGORIES.filter((cat) =>
        cat.sectionIds.some((id) => localizedSections.some((s) => s.id === id))
      ),
    [localizedSections]
  );

  // Fall back to "All" if the selected category isn't available to this user.
  const effectiveCategory =
    activeCategory !== 'all' && visibleCategories.some((c) => c.id === activeCategory)
      ? activeCategory
      : 'all';

  const normalisedQuery = query.trim().toLowerCase();
  const searchTokens = useMemo(() => tokenizeQuery(normalisedQuery), [normalisedQuery]);
  const highlightTerms = useMemo(
    () => (searchTokens.length ? buildHighlightTerms(searchTokens) : []),
    [searchTokens]
  );

  const filteredSections = useMemo(() => {
    // Search is global — it ignores the active category so a direct lookup
    // always finds the answer wherever it lives.
    if (searchTokens.length > 0) {
      return orderedSections.filter((s) =>
        sectionMatchesQuery(sectionHaystack(s), searchTokens)
      );
    }
    if (effectiveCategory === 'all') return orderedSections;
    const cat = USER_GUIDE_CATEGORIES.find((c) => c.id === effectiveCategory);
    return cat ? orderedSections.filter((s) => cat.sectionIds.includes(s.id)) : orderedSections;
  }, [orderedSections, searchTokens, effectiveCategory]);

  // While searching, auto-expand matches so answers are visible immediately.
  const isSearching = searchTokens.length > 0;
  const isOpen = useCallback(
    (id) => (isSearching ? true : openIds.has(id)),
    [isSearching, openIds]
  );

  const toggle = useCallback((id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Expand / collapse act on the sections currently shown (respects the
  // active category tab).
  const expandAll = useCallback(() => {
    setOpenIds(new Set(filteredSections.map((s) => s.id)));
  }, [filteredSections]);

  const collapseAll = useCallback(() => setOpenIds(new Set()), []);

  const allExpanded =
    filteredSections.length > 0 && filteredSections.every((s) => openIds.has(s.id));

  return (
    <DashboardLayout>
      <div lang={locale} className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        {/* Page Header — DS compact employee header */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-blue-800" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">{ui.title}</h1>
              </div>
              <p className="text-sm text-gray-500 ml-12">{ui.subtitle}</p>
            </div>

            {/* Language switcher (DS PillSelect). Untranslated languages show
                as disabled "(soon)" entries, so nothing renders as broken
                English-in-disguise. */}
            <div className="flex-shrink-0">
              <PillSelect
                options={languageOptions}
                value={locale}
                onChange={changeLocale}
                icon={Languages}
                iconColor="text-blue-600"
                theme="employee"
                minWidth="min-w-[150px]"
                compactOnMobile
              />
            </div>
          </div>

          {/* Search — DS Type 8: DebouncedSearchInput, max-w-md */}
          <DebouncedSearchInput
            value={query}
            onSearch={setQuery}
            placeholder={ui.searchPlaceholder}
            theme="employee"
            className="mt-5 max-w-md"
          />

          {/* Result count while searching */}
          {isSearching && (
            <p className="mt-2 px-1 text-xs text-gray-500" aria-live="polite">
              {filteredSections.length === 0
                ? ui.noMatchingTopics
                : ui.searchResults(filteredSections.length, query.trim())}
            </p>
          )}

          {hasEnglishFallbackTopics && (
            <div className="mt-4 flex max-w-2xl gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-blue-800">
              <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" aria-hidden="true" />
              <p className="text-sm leading-relaxed">{ui.englishFallbackNotice}</p>
            </div>
          )}
        </div>

        {/* Category tabs — hidden while searching (search is global) */}
        {!isSearching && (
          <div className="flex items-center gap-3">
            <div
              className="flex flex-1 items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth"
              role="tablist"
              aria-label={ui.guideCategories}
            >
              {[{ id: 'all', label: ui.allTopics }, ...visibleCategories].map((cat) => {
                const active = effectiveCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`tab-btn ${active ? 'active' : ''}`}
                  >
                    {cat.id === 'all' ? cat.label : ui.categories[cat.id] || cat.label}
                  </button>
                );
              })}
            </div>
            <button
              onClick={allExpanded ? collapseAll : expandAll}
              className="icon-button flex-shrink-0"
              title={allExpanded ? ui.collapseAll : ui.expandAll}
              aria-label={allExpanded ? ui.collapseAll : ui.expandAll}
              aria-pressed={allExpanded}
            >
              {allExpanded ? (
                <ChevronsDownUp className="w-5 h-5" />
              ) : (
                <ChevronsUpDown className="w-5 h-5" />
              )}
            </button>
          </div>
        )}

        {/* Sections */}
        <div className="space-y-4">
          {filteredSections.length === 0 ? (
            <div className="card">
              <div className="card-content text-center py-10">
                <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-subsection-header">{ui.noResultsTitle}</p>
                <p className="text-body mt-1">
                  {ui.tryDifferentWord}{' '}
                  <button onClick={() => setQuery('')} className="text-blue-600 hover:underline font-medium">
                    {ui.clearSearch}
                  </button>
                  .
                </p>
              </div>
            </div>
          ) : (
            filteredSections.map((section) => (
              <GuideSection
                key={section.id}
                section={section}
                expanded={isOpen(section.id)}
                onToggle={() => toggle(section.id)}
                terms={highlightTerms}
                ui={ui}
                registerRef={(el) => {
                  sectionRefs.current[section.id] = el;
                }}
              />
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserGuide;
